import { useEffect, useState } from 'react';
import { X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${window.location.origin}/api`;
const STORAGE_KEY = 'jsf_exit_intent_shown';

/**
 * #15 — Exit-intent coupon capture.
 * Fires on first-time visitor when their cursor races toward the close-tab area.
 * Shows once per 7 days. Mobile fallback: triggers on history.back gesture.
 */
export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const last = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
    if (last && Date.now() - last < SEVEN_DAYS) return;

    let shown = false;
    let timer;
    const trigger = () => {
      if (shown) return;
      // Don't fire on admin/login/checkout/conversion-funnel routes (apply, tester kit, etc.)
      const path = window.location.pathname;
      if (/^\/(admin|login|checkout|cart|register|forgot-password|ambassador-apply|tester-kit-checkout|pending-payment|become-ambassador|become-distributor|certificate)/i.test(path)) return;
      shown = true;
      setOpen(true);
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    };

    const onMouseLeave = (e) => {
      // Cursor exiting the top of the viewport
      if (e.clientY <= 0) trigger();
    };

    // Mobile fallback: 35s of engaged scroll then trigger once
    const onScroll = () => {
      if (window.scrollY > 1200) {
        clearTimeout(timer);
        timer = setTimeout(trigger, 25000);
      }
    };

    document.addEventListener('mouseleave', onMouseLeave);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { toast.error('Please enter a valid email'); return; }
    setSubmitting(true);
    try {
      // Best-effort capture (silent failure if endpoint doesn't exist yet)
      await axios.post(`${API}/marketing/coupon-capture`, { email, source: 'exit_intent' }).catch(() => {});
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in-up"
      data-testid="exit-intent-popup"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-[#141414] border border-[#D4AF37]/40 max-w-md w-full p-7 sm:p-9 relative shadow-[0_0_60px_rgba(212,175,55,0.15)]"
      >
        <button
          onClick={() => setOpen(false)}
          data-testid="exit-intent-close"
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {!submitted ? (
          <>
            <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center mb-4">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-sans mb-2">Wait — one moment</p>
            <h3 className="text-3xl text-white font-light leading-tight mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              10% off your first scent
            </h3>
            <p className="text-white/60 text-sm font-sans leading-relaxed mb-5">
              Drop your email and we'll send a one-time code, plus the occasional new-launch heads-up. No spam, just scent.
            </p>
            <form onSubmit={submit} className="space-y-3">
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="exit-intent-email"
                required
                className="w-full bg-[#0A0A0A] border border-white/10 px-4 py-3 text-white text-sm font-sans focus:outline-none focus:border-[#D4AF37]/50"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="exit-intent-submit"
                className="btn-shimmer w-full py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-sans font-medium hover:bg-[#E5C75A] transition-colors disabled:opacity-50"
              >
                {submitting ? 'Sending…' : 'Send my code'}
              </button>
            </form>
            <p className="text-white/30 text-[11px] font-sans mt-3">No thanks, I'll pay full price 🥲</p>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-12 h-12 bg-[#1C7D3F]/15 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-5 h-5 text-[#1C7D3F]" />
            </div>
            <h3 className="text-2xl text-white font-light mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Check your inbox ✨</h3>
            <p className="text-white/60 text-sm font-sans">Your code is on its way. Enjoy the scent.</p>
          </div>
        )}
      </div>
    </div>
  );
}
